import { useCallback } from 'react';
import type { AssistantUiAction } from '@/api/assistant/assistantApi.types';
import { useAssistantTurn, type AssistantAutomaticActionResult } from '@/hooks/useAssistantTurn';
import { useAssistantPanel } from './assistantPanelContext';
import { isSmallScreen } from './mediaQueries';
import {
  AUTOMATIC_ACTION_REQUEST,
  useAssistantUiActionExecutor,
  type AssistantUiActionOutcome,
} from './uiActions/useAssistantUiActionExecutor';

function toAutomaticActionResult(outcome: AssistantUiActionOutcome): AssistantAutomaticActionResult {
  if (outcome === 'needs_confirmation') {
    return 'unsaved_changes';
  }
  return outcome;
}

export function useAssistantChatTurn() {
  const { conversationId, selectConversation, isAutoNavigateEnabled, isOpen, close } =
    useAssistantPanel();
  const executeAction = useAssistantUiActionExecutor();
  const handleUiAction = useCallback(
    async function performAction(
      action: AssistantUiAction,
      turnConversationId: string | null
    ): Promise<AssistantAutomaticActionResult> {
      if (
        isAutoNavigateEnabled === false ||
        isOpen === false ||
        turnConversationId !== conversationId
      ) {
        return 'skipped';
      }
      const outcome = await executeAction(action, AUTOMATIC_ACTION_REQUEST);
      if (outcome === 'performed' && isSmallScreen()) {
        close();
      }
      return toAutomaticActionResult(outcome);
    },
    [close, conversationId, executeAction, isAutoNavigateEnabled, isOpen]
  );
  const handleConversationCreated = useCallback(
    function rememberConversation(createdConversationId: string) {
      selectConversation(createdConversationId);
    },
    [selectConversation]
  );
  return useAssistantTurn({
    conversationId,
    onConversationCreated: handleConversationCreated,
    onUiAction: handleUiAction,
  });
}
